import type SocketManager from './SocketManager';
import type {
  FeedItem,
  LivefireAuthenticationFailedEventDetail,
  LivefireConnectErrorEventDetail,
  LivefireNewItemEventDetail,
  LivefireReconnectAttemptEventDetail,
  LivefireReconnectFailedEventDetail,
  LivefireSocketEventDetail,
} from './types';

function buildSocketEvent<T extends LivefireSocketEventDetail = LivefireSocketEventDetail>(
  socket: SocketManager,
): T {
  const { clientId, token, url } = socket || {};
  return {
    clientId,
    error: null,
    socket,
    token,
    url,
  } as T;
}

function buildConnectEvent<ErrorType = unknown, ReasonType = unknown>(
  socket: SocketManager,
  {
    error = null,
    reason = null,
  }: Partial<{ error: ErrorType | null; reason: ReasonType | null }> = {},
) {
  const detail = buildSocketEvent<LivefireConnectErrorEventDetail>(socket);
  detail.error = error;
  detail.reason = reason;
  return detail;
}

function buildReconnectEvent<T extends LivefireReconnectAttemptEventDetail>(
  socket: SocketManager,
  { attempt = -1, error = null }: Partial<{ attempt: number; error: unknown }>,
) {
  const detail = buildSocketEvent<T>(socket);
  detail.attempt = attempt;
  detail.error = error;
  return detail;
}

function createEvent<T>(type: string, eventDetail: T) {
  const detail = Object.freeze({ ...eventDetail });
  return new CustomEvent(type, { detail });
}

export function authenticationFailedEvent<T>(socket: SocketManager, data: T | null = null) {
  const detail = buildSocketEvent<LivefireAuthenticationFailedEventDetail>(socket);
  detail.data = data || null;

  return createEvent('authentication_failed', detail);
}

export function cloneEvent(evt: CustomEvent) {
  return createEvent(evt.type, evt.detail);
}

export function connectErrorEvent<T>(socket: SocketManager, error: T) {
  const detail = buildConnectEvent(socket, { error });
  return createEvent('connect_error', detail);
}

export function connectEvent(socket: SocketManager) {
  const detail = buildConnectEvent(socket);
  return createEvent('connect', detail);
}

export function disconnectEvent<T>(socket: SocketManager, reason: T) {
  const detail = buildConnectEvent(socket, { reason });
  return createEvent('disconnect', detail);
}

export function newFeedItemEvent(feedItem: FeedItem) {
  if (typeof feedItem !== 'object' || feedItem === null) {
    throw new Error('must provide an object for feedItem');
  }

  const detail = { feedItem };
  return createEvent('newFeedItem', detail);
}

export function newSocketItemEvent(socket: SocketManager, item: FeedItem) {
  if (typeof item !== 'object' || item === null) {
    throw new Error('must provide an object for item');
  }

  const detail = buildSocketEvent<LivefireNewItemEventDetail>(socket);
  detail.item = item;

  return createEvent('newItem', detail);
}

export function reconnectAttemptEvent(socket: SocketManager, attempt: number) {
  const detail = buildReconnectEvent(socket, { attempt });
  return createEvent('reconnect_attempt', detail);
}

export function reconnectErrorEvent<T>(socket: SocketManager, error: T | null = null) {
  const detail = buildReconnectEvent(socket, { error });
  return createEvent('reconnect_error', detail);
}

export function reconnectFailedEvent(
  socket: SocketManager,
  attempt: number = 0,
  error: unknown | null = null,
) {
  const detail = buildReconnectEvent<LivefireReconnectFailedEventDetail>(socket, {
    attempt,
    error,
  });
  return createEvent('reconnect_failed', detail);
}
